import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

/**
 * Handles Supabase auth callbacks (email confirmation, password reset, etc.)
 * After processing the callback, redirects to /app
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading } = useAuth();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Defensive: Handle both hash fragments (Supabase default) and query params (fallback)
        const hash = window.location.hash.substring(1);
        const search = window.location.search.substring(1);
        const params = hash ? new URLSearchParams(hash) : new URLSearchParams(search);
        
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');
        const error = params.get('error');
        const errorDescription = params.get('error_description');

        // Handle error from callback
        if (error) {
          console.error('[AuthCallback] Error:', error, errorDescription);
          const errorMsg = errorDescription || error || 'Authentication failed';
          navigate('/auth?error=' + encodeURIComponent(errorMsg), { replace: true });
          return;
        }

        // If we have tokens, exchange them for a session
        if (accessToken && refreshToken) {
          const { data, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (sessionError) {
            console.error('[AuthCallback] Session error:', sessionError);
            navigate('/auth?error=' + encodeURIComponent(sessionError.message || 'Failed to create session'), { replace: true });
            return;
          }

          // Session set successfully, redirect to app
          if (data?.session) {
            navigate('/app', { replace: true });
            return;
          }
        }

        // Check if we already have a session (user might have been redirected here)
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          navigate('/app', { replace: true });
          return;
        }

        // No session found, redirect to auth
        navigate('/auth', { replace: true });
      } catch (err: any) {
        console.error('[AuthCallback] Unexpected error:', err);
        const errorMsg = err?.message || err?.toString() || 'Authentication failed';
        navigate('/auth?error=' + encodeURIComponent(errorMsg), { replace: true });
      }
    };

    // Wait for auth to finish loading before processing callback
    if (!loading) {
      handleCallback();
    }
  }, [navigate, loading]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Completing authentication...</p>
      </div>
    </div>
  );
}

